import { RouteListener } from '@/services/route';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { ProfileUsers } from './api/enumerations';
import { isLegacyEnabled } from './api/legacy/config';
import { getLoginLayout } from './api/login';
import { LoginLayoutData } from './api/login/types';
import avaliaHubLockup from './assets/brand/avaliahub-lockup-canopy.svg';
import Menu from './components/Menu';
import { MfaGuard } from './components/MfaGuard';
import { PageFallback } from './components/PageFallback';
import ScrollToTop from './components/ScrollToTop';
import { getCompanySubdomain } from './helpers';
import { lazyNamed, lazyWithRetry } from './helpers/lazyWithRetry';
import { ACCESS_ROLES } from './helpers/roleAccess';
import { ErrorPage } from './pages/ErrorPage';
import {
  DistributionSimulator,
  ReportTemplateEditor,
} from './pages/Hive/Tabs/Notices/fullScreenPages';
import { Home } from './pages/Home';
import { Login } from './pages/Login';
import PrivateRoute from './PrivateRoute';
import { getTheme } from './styles/theme';
import { brand, tenantActionColorEnabled } from './styles/tokens';

// Route-level code splitting: everything past the login/home critical path
// loads on demand, grouped by area so one navigation fetches one chunk.
/* eslint-disable prettier/prettier, max-len */
const auth = (): Promise<typeof import('./pages/Mfa')> =>
  import(/* webpackChunkName: "auth" */ './pages/Mfa');
const Mfa = lazyNamed(auth, 'Mfa');
const ForgotPassword = lazyNamed(
  () => import(/* webpackChunkName: "auth" */ './pages/ForgotPassword'),
  'ForgotPassword'
);
const CreateAccount = lazyWithRetry(
  () => import(/* webpackChunkName: "auth" */ './pages/CreateAccount')
);
const ResetPassword = lazyWithRetry(
  () => import(/* webpackChunkName: "auth" */ './pages/ResetPassword')
);

const Entrance = lazyNamed(
  () => import(/* webpackChunkName: "os-flow" */ './pages/Entrance'),
  'Entrance'
);
const Pept = lazyNamed(
  () => import(/* webpackChunkName: "os-flow" */ './pages/Pept'),
  'Pept'
);
const Schedule = lazyNamed(
  () => import(/* webpackChunkName: "os-flow" */ './pages/Schedule'),
  'Schedule'
);
const Inspection = lazyNamed(
  () => import(/* webpackChunkName: "os-flow" */ './pages/Inspection'),
  'Inspection'
);
const SampleCreation = lazyNamed(
  () => import(/* webpackChunkName: "os-flow" */ './pages/SampleCreation'),
  'SampleCreation'
);
const Calculation = lazyNamed(
  () => import(/* webpackChunkName: "os-flow" */ './pages/Calculation'),
  'Calculation'
);
const Report = lazyNamed(
  () => import(/* webpackChunkName: "os-flow" */ './pages/Report'),
  'Report'
);
const Revision = lazyNamed(
  () => import(/* webpackChunkName: "os-flow" */ './pages/Revision'),
  'Revision'
);
const Archived = lazyNamed(
  () => import(/* webpackChunkName: "os-flow" */ './pages/Archived'),
  'Archived'
);
const CharacteristicsPage = lazyNamed(
  () => import(/* webpackChunkName: "os-flow" */ './pages/Characteristics'),
  'CharacteristicsPage'
);
const ScientificTreatmentSteps = lazyNamed(
  () =>
    import(
      /* webpackChunkName: "os-flow" */ './pages/Report/ScientificTreatment'
    ),
  'ScientificTreatmentSteps'
);

const Rooms = lazyNamed(
  () =>
    import(
      /* webpackChunkName: "os-photos" */ './pages/InspectionPhotos/Rooms'
    ),
  'Rooms'
);
const InspectionAddPhotos = lazyNamed(
  () =>
    import(
      /* webpackChunkName: "os-photos" */ './pages/InspectionPhotos/AddPhotos'
    ),
  'InspectionAddPhotos'
);
const InspectionPhotoGallery = lazyNamed(
  () =>
    import(
      /* webpackChunkName: "os-photos" */ './pages/InspectionPhotos/PhotoGallery'
    ),
  'InspectionPhotoGallery'
);
const ImageDetails = lazyNamed(
  () =>
    import(
      /* webpackChunkName: "os-photos" */ './pages/InspectionPhotos/ImageDetails'
    ),
  'ImageDetails'
);
const InspectionPhotos = lazyNamed(
  () =>
    import(
      /* webpackChunkName: "os-photos" */ './pages/Report/components/InspectionPhotos'
    ),
  'InspectionPhotos'
);

const ArchivedFiles = lazyNamed(
  () => import(/* webpackChunkName: "archive" */ './pages/ArchivedFiles'),
  'ArchivedFiles'
);
const ArchivedWorkOrders = lazyNamed(
  () =>
    import(
      /* webpackChunkName: "archive" */ './pages/ArchivedFiles/Tabs/WorkOrders'
    ),
  'ArchivedWorkOrders'
);
const LegacyList = lazyNamed(
  () =>
    import(
      /* webpackChunkName: "archive" */ './pages/ArchivedFiles/Tabs/Legacy'
    ),
  'LegacyList'
);
const LegacyRequest = lazyNamed(
  () => import(/* webpackChunkName: "archive" */ './pages/LegacyRequest'),
  'LegacyRequest'
);

const Hive = lazyNamed(
  () => import(/* webpackChunkName: "hive" */ './pages/Hive'),
  'Hive'
);
const Requests = lazyNamed(
  () => import(/* webpackChunkName: "hive" */ './pages/Hive/Tabs/Requests'),
  'Requests'
);
const Licensed = lazyNamed(
  () => import(/* webpackChunkName: "hive" */ './pages/Hive/Tabs/Licensed'),
  'Licensed'
);
const CompanyInfo = lazyNamed(
  () => import(/* webpackChunkName: "hive" */ './pages/Hive/Tabs/CompanyInfo'),
  'CompanyInfo'
);
const Notices = lazyNamed(
  () => import(/* webpackChunkName: "hive" */ './pages/Hive/Tabs/Notices'),
  'Notices'
);
const NewNotice = lazyNamed(
  () =>
    import(
      /* webpackChunkName: "hive" */ './pages/Hive/Tabs/Notices/NewNotice'
    ),
  'NewNotice'
);
const NoticeDetails = lazyNamed(
  () =>
    import(
      /* webpackChunkName: "hive" */ './pages/Hive/Tabs/Notices/NoticeDetails'
    ),
  'NoticeDetails'
);
const NoticeGroupPage = lazyNamed(
  () =>
    import(
      /* webpackChunkName: "hive" */ './pages/Hive/Tabs/Notices/NoticeDetails/Products/GroupPage'
    ),
  'NoticeGroupPage'
);
const NoticeServicePage = lazyNamed(
  () =>
    import(
      /* webpackChunkName: "hive" */ './pages/Hive/Tabs/Notices/NoticeDetails/Products/ServicePage'
    ),
  'NoticeServicePage'
);
const AddCompany = lazyNamed(
  () =>
    import(
      /* webpackChunkName: "hive" */ './pages/Hive/Tabs/Notices/AddCompany'
    ),
  'AddCompany'
);
const LicenseRequest = lazyNamed(
  () =>
    import(
      /* webpackChunkName: "hive" */ './pages/Hive/Tabs/Notices/LicenseRequest'
    ),
  'LicenseRequest'
);

const RedeProto = lazyNamed(
  () =>
    import(/* webpackChunkName: "rede-proto" */ './pages/Hive/Tabs/RedeProto'),
  'RedeProto'
);
const ComplianceCases = lazyNamed(
  () =>
    import(
      /* webpackChunkName: "rede-proto" */ './pages/Hive/Tabs/RedeProto/screens/ComplianceCases'
    ),
  'ComplianceCases'
);
const Recruitment = lazyNamed(
  () =>
    import(
      /* webpackChunkName: "rede-proto" */ './pages/Hive/Tabs/RedeProto/screens/Recruitment'
    ),
  'Recruitment'
);
const InAccreditation = lazyNamed(
  () =>
    import(
      /* webpackChunkName: "rede-proto" */ './pages/Hive/Tabs/RedeProto/screens/InAccreditation'
    ),
  'InAccreditation'
);
const Overview = lazyNamed(
  () =>
    import(
      /* webpackChunkName: "rede-proto" */ './pages/Hive/Tabs/RedeProto/screens/Overview'
    ),
  'Overview'
);
const Bsc = lazyNamed(
  () =>
    import(
      /* webpackChunkName: "rede-proto" */ './pages/Hive/Tabs/RedeProto/screens/Bsc'
    ),
  'Bsc'
);
const NetworkMap = lazyNamed(
  () =>
    import(
      /* webpackChunkName: "rede-proto" */ './pages/Hive/Tabs/RedeProto/screens/NetworkMap'
    ),
  'NetworkMap'
);
const CriticalPoles = lazyNamed(
  () =>
    import(
      /* webpackChunkName: "rede-proto" */ './pages/Hive/Tabs/RedeProto/screens/CriticalPoles'
    ),
  'CriticalPoles'
);
const CustomerSuccess = lazyNamed(
  () =>
    import(
      /* webpackChunkName: "rede-proto" */ './pages/Hive/Tabs/RedeProto/screens/CustomerSuccess'
    ),
  'CustomerSuccess'
);
const Evolution = lazyNamed(
  () =>
    import(
      /* webpackChunkName: "rede-proto" */ './pages/Hive/Tabs/RedeProto/screens/Evolution'
    ),
  'Evolution'
);
const Funnel = lazyNamed(
  () =>
    import(
      /* webpackChunkName: "rede-proto" */ './pages/Hive/Tabs/RedeProto/screens/Funnel'
    ),
  'Funnel'
);
const Typologies = lazyNamed(
  () =>
    import(
      /* webpackChunkName: "rede-proto" */ './pages/Hive/Tabs/RedeProto/screens/Typologies'
    ),
  'Typologies'
);
const Goals = lazyNamed(
  () =>
    import(
      /* webpackChunkName: "rede-proto" */ './pages/Hive/Tabs/RedeProto/screens/Goals'
    ),
  'Goals'
);

const Dashboard = lazyNamed(
  () => import(/* webpackChunkName: "dashboard" */ './pages/Dashboard'),
  'Dashboard'
);
const PreFlight = lazyNamed(
  () => import(/* webpackChunkName: "preflight" */ './pages/PreFlight'),
  'PreFlight'
);
const Forms = lazyNamed(
  () => import(/* webpackChunkName: "forms" */ './pages/Forms'),
  'Forms'
);
const FormCreation = lazyNamed(
  () => import(/* webpackChunkName: "forms" */ './pages/Forms/FormCreation'),
  'FormCreation'
);
const UsersBankRoles = lazyNamed(
  () => import(/* webpackChunkName: "admin" */ './pages/UsersBankRoles'),
  'UsersBankRoles'
);
const Finance = lazyNamed(
  () => import(/* webpackChunkName: "admin" */ './pages/Finance'),
  'Finance'
);
const Notifications = lazyNamed(
  () => import(/* webpackChunkName: "admin" */ './pages/Notifications'),
  'Notifications'
);
/* eslint-enable prettier/prettier, max-len */

export default function App(): JSX.Element {
  const [loadingLayout, setLoadingLayout] = useState(true);
  const [themeConfig, setThemeConfig] = useState<LoginLayoutData>();
  const companySubdomain = getCompanySubdomain();
  const isRealPrice = companySubdomain.includes('realprice');

  const getCompanyLayout = useCallback(async () => {
    try {
      const layoutResponse = await getLoginLayout();

      if (layoutResponse.detail.description) {
        throw new Error(layoutResponse.detail.description);
      }

      if (layoutResponse.data) {
        setThemeConfig(layoutResponse.data);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn(error);
      }
    } finally {
      setLoadingLayout(false);
    }
  }, []);

  useEffect(() => {
    getCompanyLayout();
  }, [getCompanyLayout]);

  // The tenant primary drives the action color only when the flag in
  // tokens.ts allows it; otherwise every tenant gets the Avaliahub brand.
  // Memoized: a new theme object re-styles the whole MUI tree.
  const theme = useMemo(
    () =>
      getTheme({
        primary: {
          main:
            (tenantActionColorEnabled && themeConfig?.primary_color) ||
            brand.primary,
        },
        secondary: { main: themeConfig?.secondary_color || brand.deepGreen },
      }),
    [themeConfig?.primary_color, themeConfig?.secondary_color]
  );

  if (loadingLayout) {
    return <PageFallback fullScreen />;
  }

  // The product brand (AvaliaHub) is fixed; a tenant logo, when the tenant
  // has one, is shown as a secondary mark inside the auth card.
  const tenantLogo = isRealPrice
    ? undefined
    : themeConfig?.logo_url || undefined;
  // CreateAccount still renders the legacy layout and needs a logo URL.
  const accountLogo = tenantLogo || avaliaHubLockup;
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <ScrollToTop />

        <RouteListener />

        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/" element={<Login tenantLogo={tenantLogo} />} />
            <Route path="/login" element={<Login tenantLogo={tenantLogo} />} />
            <Route path="/mfa" element={<Mfa tenantLogo={tenantLogo} />} />
            <Route
              path="/forgot-password"
              element={<ForgotPassword tenantLogo={tenantLogo} />}
            />
            <Route
              path="create-account"
              element={<CreateAccount logo={accountLogo} />}
            />
            <Route
              path="new-password"
              element={<ResetPassword tenantLogo={tenantLogo} />}
            />
            <Route
              path="reset-password"
              element={<ResetPassword tenantLogo={tenantLogo} />}
            />
            <Route
              element={
                <MfaGuard>
                  <Menu />
                </MfaGuard>
              }
            >
              <Route
                path="/home"
                element={
                  <PrivateRoute>
                    <Home />
                  </PrivateRoute>
                }
              />
              <Route
                path="/home/property/new"
                element={
                  <PrivateRoute>
                    <Entrance />
                  </PrivateRoute>
                }
              />
              <Route
                path="/home/property/:id/edit"
                element={
                  <PrivateRoute>
                    <Entrance />
                  </PrivateRoute>
                }
              />
              <Route
                path="/home/property/:id/pept"
                element={
                  <PrivateRoute>
                    <Pept />
                  </PrivateRoute>
                }
              />
              <Route
                path="/home/property/:id/schedule"
                element={
                  <PrivateRoute>
                    <Schedule />
                  </PrivateRoute>
                }
              />
              <Route
                path="/home/property/:id/inspection"
                element={
                  <PrivateRoute>
                    <Inspection />
                  </PrivateRoute>
                }
              />
              <Route
                path="/home/property/:id/inspection/:inspection/rooms"
                element={
                  <PrivateRoute>
                    <Rooms />
                  </PrivateRoute>
                }
              />
              <Route
                path="/home/property/:id/inspection/:inspection/room/:room/add-photos"
                element={
                  <PrivateRoute>
                    <InspectionAddPhotos />
                  </PrivateRoute>
                }
              />
              <Route
                path="/home/property/:id/inspection/:inspection/room/:room/gallery"
                element={
                  <PrivateRoute>
                    <InspectionPhotoGallery />
                  </PrivateRoute>
                }
              />
              <Route
                // eslint-disable-next-line max-len
                path="/home/property/:id/inspection/:inspection/room/:room/image-details/new/:imageId"
                element={
                  <PrivateRoute>
                    <ImageDetails />
                  </PrivateRoute>
                }
              />
              <Route
                path="/home/property/:id/inspection/:inspection/room/:room/image-details/:imageId"
                element={
                  <PrivateRoute>
                    <ImageDetails />
                  </PrivateRoute>
                }
              />
              <Route
                path="/home/property/:id/sample"
                element={
                  <PrivateRoute>
                    <SampleCreation />
                  </PrivateRoute>
                }
              />
              <Route
                path="/home/property/:id/report"
                element={
                  <PrivateRoute>
                    <Report />
                  </PrivateRoute>
                }
              />
              <Route
                path="/home/property/:id/report/characteristics"
                element={<CharacteristicsPage />}
              />
              <Route
                path="/home/property/:id/report/scientific-treatment"
                element={<ScientificTreatmentSteps />}
              />
              <Route
                path="/home/property/:id/report/:inspection/inspection-photos"
                element={
                  <PrivateRoute>
                    <InspectionPhotos />
                  </PrivateRoute>
                }
              />
              <Route
                path="/home/property/:id/calculation"
                element={
                  <PrivateRoute>
                    <Calculation />
                  </PrivateRoute>
                }
              />
              <Route
                path="/home/property/:id/revision"
                element={
                  <PrivateRoute>
                    <Revision />
                  </PrivateRoute>
                }
              />
              <Route
                path="/home/property/:id/revision/characteristics"
                element={<CharacteristicsPage />}
              />
              <Route
                path="/home/property/:id/archived"
                element={
                  <PrivateRoute>
                    <Archived />
                  </PrivateRoute>
                }
              />
              <Route
                path="/home/property/:id/archived/characteristics"
                element={<CharacteristicsPage />}
              />
              <Route
                path="/archived"
                element={
                  <PrivateRoute
                    allowedRoles={[
                      ProfileUsers.BANK_RESPONSAVEL_TECNICO,
                      ProfileUsers.BANK_APENAS_LEITURA,
                    ]}
                  >
                    <ArchivedFiles />
                  </PrivateRoute>
                }
              >
                <Route path="" element={<ArchivedWorkOrders />} />
                {isLegacyEnabled && (
                  <Route path="legacy" element={<LegacyList />} />
                )}
              </Route>
              {isLegacyEnabled && (
                <Route
                  path="/archived/legacy/:docId"
                  element={
                    <PrivateRoute
                      allowedRoles={[
                        ProfileUsers.BANK_RESPONSAVEL_TECNICO,
                        ProfileUsers.BANK_APENAS_LEITURA,
                      ]}
                    >
                      <LegacyRequest />
                    </PrivateRoute>
                  }
                />
              )}
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute allowedRoles={[]}>
                    <Dashboard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/users-roles"
                element={
                  <PrivateRoute allowedRoles={[]}>
                    <UsersBankRoles />
                  </PrivateRoute>
                }
              />
              <Route
                path="/forms"
                element={
                  <PrivateRoute>
                    <Forms />
                  </PrivateRoute>
                }
              />
              <Route
                path="/forms/new"
                element={
                  <PrivateRoute>
                    <FormCreation />
                  </PrivateRoute>
                }
              />
              <Route
                path="/forms/:id/edit"
                element={
                  <PrivateRoute>
                    <FormCreation />
                  </PrivateRoute>
                }
              />
              <Route
                path="pre-flight"
                element={
                  <PrivateRoute
                    allowedRoles={[ProfileUsers.BANK_RESPONSAVEL_TECNICO]}
                  >
                    <PreFlight />
                  </PrivateRoute>
                }
              />
              {/* Full screen as well; same access as the distribution tab. */}
              <Route
                path="the-hive/notices/:notice/distribution-simulator"
                element={
                  <PrivateRoute allowedRoles={ACCESS_ROLES.noticeDistribution}>
                    <DistributionSimulator />
                  </PrivateRoute>
                }
              />
              {/* Full screen: outside "the-hive" so it skips the Hive header and tabs. */}
              <Route
                // eslint-disable-next-line max-len
                path="the-hive/notices/:notice/products/groups/:group/services/:service/report-template"
                element={
                  <PrivateRoute
                    allowedRoles={[
                      ProfileUsers.BANK_RESPONSAVEL_TECNICO,
                      ProfileUsers.CS,
                      ProfileUsers.FINANCEIRO,
                      ProfileUsers.JURIDICO,
                      // pair capacity + analysis queues (PROT-2950): bank
                      // managers and RealPrice engineers own these Hive screens
                      ProfileUsers.BANK_GESTOR_EDITAIS,
                      ProfileUsers.BANK_GESTOR_CREDENCIADO,
                      ProfileUsers.ENGINEER,
                    ]}
                  >
                    <ReportTemplateEditor />
                  </PrivateRoute>
                }
              />
              <Route
                path="the-hive"
                element={
                  <PrivateRoute
                    allowedRoles={[
                      ProfileUsers.BANK_RESPONSAVEL_TECNICO,
                      ProfileUsers.CS,
                      ProfileUsers.FINANCEIRO,
                      ProfileUsers.JURIDICO,
                      // pair capacity + analysis queues (PROT-2950): bank
                      // managers and RealPrice engineers own these Hive screens
                      ProfileUsers.BANK_GESTOR_EDITAIS,
                      ProfileUsers.BANK_GESTOR_CREDENCIADO,
                      ProfileUsers.ENGINEER,
                    ]}
                  >
                    <Hive />
                  </PrivateRoute>
                }
              >
                <Route path="" element={<Requests />} />
                <Route path="requests" element={<Requests />} />
                <Route path="licensed">
                  <Route path="" element={<Licensed />} />
                  <Route path=":notice" element={<NoticeDetails />} />
                  <Route path="company-details/:company">
                    <Route path="" element={<CompanyInfo />} />
                    <Route
                      path="notices/:notice/request/:request"
                      element={<LicenseRequest />}
                    />
                  </Route>
                </Route>
                <Route path="notices">
                  <Route path="" element={<Notices />} />
                  <Route path="new" element={<NewNotice />} />
                  <Route path=":notice" element={<NoticeDetails />} />
                  <Route path=":notice/edit" element={<NewNotice />} />
                  <Route
                    path=":notice/products/groups/:group"
                    element={<NoticeGroupPage />}
                  />
                  <Route
                    path=":notice/products/groups/:group/services/new"
                    element={<NoticeServicePage />}
                  />
                  <Route
                    path=":notice/products/groups/:group/services/:service"
                    element={<NoticeServicePage />}
                  />
                  <Route path=":notice/add-company" element={<AddCompany />} />
                  <Route
                    path=":notice/request/:request"
                    element={<LicenseRequest />}
                  />
                </Route>
                <Route path="rede-proto" element={<RedeProto />}>
                  <Route path="" element={<InAccreditation />} />
                  <Route path="preenchimento" element={<Recruitment />} />
                  <Route path="regularizacao" element={<ComplianceCases />} />
                  <Route path="visao-geral" element={<Overview />} />
                  <Route path="bsc" element={<Bsc />} />
                  <Route path="mapa" element={<NetworkMap />} />
                  <Route path="polos-criticos" element={<CriticalPoles />} />
                  <Route
                    path="customer-success"
                    element={<CustomerSuccess />}
                  />
                  <Route path="evolucao" element={<Evolution />} />
                  <Route path="captacao" element={<Funnel />} />
                  <Route path="tipologias" element={<Typologies />} />
                  <Route path="metas" element={<Goals />} />
                </Route>
              </Route>
              <Route
                path="/notifications"
                element={
                  <PrivateRoute>
                    <Notifications />
                  </PrivateRoute>
                }
              />
              <Route
                path="/finance"
                element={
                  <PrivateRoute allowedRoles={[ProfileUsers.FINANCEIRO]}>
                    <Finance />
                  </PrivateRoute>
                }
              />
            </Route>
            <Route path="*" element={<ErrorPage />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ThemeProvider>
  );
}
